import React, { useState, useEffect, useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import { Heart, TrendingUp, Calendar, ArrowUpRight, IndianRupee } from "lucide-react";
import { formatCurrency, COMMITTEE } from "../utils/mockData";
import { eventsApi } from "../utils/api";

const EXPENSE_COLORS = ["#f97316", "#fb923c", "#f59e0b", "#d97706", "#b45309", "#92400e", "#fdba74", "#fde68a", "#fbbf24", "#fed7aa"];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-lg p-3">
        <p className="text-xs font-semibold text-gray-500 mb-1">{label}</p>
        <p className="text-sm font-bold text-orange-600">{formatCurrency(payload[0].value)}</p>
      </div>
    );
  }
  return null;
};

export default function Dashboard({ setActivePage, donations = [], expenses = [] }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    eventsApi.list().then((res) => setEvents(res.items || [])).catch(console.error);
  }, []);

  const totalDonations = donations.reduce((s, d) => s + Number(d.amount || 0), 0);
  const totalExpenses = expenses.reduce((s, e) => s + Number(e.amount || 0), 0);
  const netBalance = totalDonations - totalExpenses;

  const stats = [
    {
      label: "Total Donations",
      value: formatCurrency(totalDonations),
      sub: `${donations.length} donors`,
      icon: Heart,
      light: "bg-orange-50 text-orange-600",
      trend: `${donations.length}`,
    },
    {
      label: "Total Expenses",
      value: formatCurrency(totalExpenses),
      sub: `${expenses.length} entries`,
      icon: TrendingUp,
      light: "bg-blue-50 text-blue-600",
      trend: `${expenses.length}`,
    },
    {
      label: "Net Balance",
      value: formatCurrency(netBalance),
      sub: "Available funds",
      icon: IndianRupee,
      light: "bg-green-50 text-green-600",
      trend: netBalance >= 0 ? "+" : "-",
    },
    {
      label: "Events",
      value: events.length,
      sub: `${events.filter((e) => e.status === "upcoming").length} upcoming`,
      icon: Calendar,
      light: "bg-purple-50 text-purple-600",
      trend: `${events.length}`,
    },
  ];

  const chartData = useMemo(() => {
    const byDate = {};
    donations.forEach((d) => {
      const date = d.date || "";
      if (!date) return;
      if (!byDate[date]) byDate[date] = { amount: 0 };
      byDate[date].amount += Number(d.amount || 0);
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, data]) => ({
        day: new Date(date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" }),
        amount: data.amount,
      }));
  }, [donations]);

  const expensePieData = useMemo(() => {
    const byCategory = {};
    expenses.forEach((e) => {
      const cat = e.category || "Other";
      byCategory[cat] = (byCategory[cat] || 0) + Number(e.amount || 0);
    });
    return Object.entries(byCategory).map(([name, value], i) => ({
      name,
      value,
      color: EXPENSE_COLORS[i % EXPENSE_COLORS.length],
    }));
  }, [expenses]);

  const upcomingEvents = events.slice(0, 4);
  const recentDonations = donations.slice(0, 5);

  const daysLeft = Math.ceil(
    (new Date(COMMITTEE.festivalStart) - new Date()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="p-4 lg:p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Ganesh Chaturthi 2026</h1>
          <p className="text-sm text-gray-500 mt-0.5">{COMMITTEE.name} • {COMMITTEE.festivalDays} days festival</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-orange-50 border border-orange-200 rounded-xl px-4 py-2 text-center">
            <p className="text-2xl font-black text-orange-600">{daysLeft > 0 ? daysLeft : "Live!"}</p>
            <p className="text-[10px] text-orange-500 font-medium uppercase tracking-wide">
              {daysLeft > 0 ? "Days to Festival" : "Festival is Live"}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${stat.light}`}>
                  <Icon size={17} />
                </div>
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                  <ArrowUpRight size={10} /> {stat.trend}
                </span>
              </div>
              <p className="text-lg lg:text-xl font-black text-gray-900">{stat.value}</p>
              <p className="text-xs font-semibold text-gray-500 mt-0.5">{stat.label}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{stat.sub}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Area Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-sm font-bold text-gray-900">Daily Collections</h2>
              <p className="text-xs text-gray-400">Donations by date</p>
            </div>
            <span className="text-xs text-orange-500 font-semibold bg-orange-50 px-2.5 py-1 rounded-full">
              {formatCurrency(totalDonations)} total
            </span>
          </div>
          {chartData.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-16">No donations recorded yet</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="orange" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} tickLine={false} axisLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="amount" stroke="#f97316" strokeWidth={2.5}
                  fill="url(#orange)" dot={{ fill: "#f97316", r: 3 }} activeDot={{ r: 5 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Pie Chart */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="mb-3">
            <h2 className="text-sm font-bold text-gray-900">Expense Breakdown</h2>
            <p className="text-xs text-gray-400">{formatCurrency(totalExpenses)} total</p>
          </div>
          {expensePieData.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-12">No expenses recorded yet</p>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={expensePieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70}
                    dataKey="value" stroke="none">
                    {expensePieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-1">
                {expensePieData.slice(0, 4).map((item) => (
                  <div key={item.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-gray-600">{item.name}</span>
                    </div>
                    <span className="font-semibold text-gray-700">{formatCurrency(item.value)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Donations */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-900">Recent Donations</h2>
            <button onClick={() => setActivePage("donations")}
              className="text-xs text-orange-500 hover:text-orange-600 font-semibold flex items-center gap-1">
              View All <ArrowUpRight size={12} />
            </button>
          </div>
          {recentDonations.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No donations recorded yet</p>
          ) : (
            <div className="space-y-2">
              {recentDonations.map((d) => (
                <div key={d.id} className="flex items-center gap-3 p-2.5 hover:bg-gray-50 rounded-xl transition-colors">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-sm font-bold text-orange-600 flex-shrink-0">
                    {d.donorName.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{d.donorName}</p>
                    <p className="text-[10px] text-gray-400">{d.paymentMode} • {d.purpose}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-green-600">{formatCurrency(Number(d.amount || 0))}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      d.status === "completed" ? "bg-green-50 text-green-600" : "bg-yellow-50 text-yellow-600"
                    }`}>
                      {d.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-gray-900">Upcoming Events</h2>
            <button onClick={() => setActivePage("events")}
              className="text-xs text-orange-500 hover:text-orange-600 font-semibold flex items-center gap-1">
              View All <ArrowUpRight size={12} />
            </button>
          </div>
          {upcomingEvents.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No events added yet</p>
          ) : (
            <div className="space-y-2">
              {upcomingEvents.map((e) => (
                <div key={e.id} className="flex items-center gap-3 p-2.5 hover:bg-gray-50 rounded-xl transition-colors">
                  <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    e.type === "Pooja" ? "bg-orange-100" :
                    e.type === "Cultural" ? "bg-purple-100" :
                    e.type === "Competition" ? "bg-blue-100" : "bg-green-100"
                  }`}>
                    <Calendar size={14} className={
                      e.type === "Pooja" ? "text-orange-600" :
                      e.type === "Cultural" ? "text-purple-600" :
                      e.type === "Competition" ? "text-blue-600" : "text-green-600"
                    } />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{e.name}</p>
                    <p className="text-[10px] text-gray-400">{e.time} • {e.venue}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      e.type === "Pooja" ? "bg-orange-50 text-orange-600" :
                      e.type === "Cultural" ? "bg-purple-50 text-purple-600" :
                      e.type === "Competition" ? "bg-blue-50 text-blue-600" : "bg-green-50 text-green-600"
                    }`}>
                      {e.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
